import { useQuery } from 'react-query';
import { openerOrchestratorAxiosApi } from 'src/Config/axiosApi';

/**
 * The engine feature-flag registry, served by opener-orchestrator.
 *
 * The vocabulary is declared once, in `packages/prediction-schemas`, in the
 * arbitrage-core repo. This app cannot import it (separate repo, not in that
 * pnpm workspace, and the package peer-depends on Nest + Mongoose), so it reads
 * it over the wire instead. That is what keeps the toggle list GENERATED:
 * declaring a new flag needs no change in this repo at all.
 */
export type FeatureFlagStatus = 'experimental' | 'rollout';

export interface FeatureFlagDef {
  key: string;
  description: string;
  /** Free label used only to group the switches. */
  area: string;
  status: FeatureFlagStatus;
  addedAt: string;
  retireBy: string;
}

export const FEATURE_FLAG_REGISTRY_KEY = ['feature-flag-registry'];

/**
 * Unwrap the `{ flags: [...] }` envelope, THROWING on any other shape.
 *
 * A non-envelope response (backend not deployed yet, a proxy error page, an
 * auth redirect) would otherwise leave `data.flags` undefined and the query
 * SUCCESSFUL — the section would then render nothing at all, which is exactly
 * the "this build has no flags" misread it exists to prevent.
 */
export function parseRegistryResponse(data: unknown): FeatureFlagDef[] {
  const flags = (data as { flags?: unknown } | null | undefined)?.flags;
  if (!Array.isArray(flags)) {
    throw new Error('Malformed feature-flag registry response');
  }
  return flags as FeatureFlagDef[];
}

export function useFeatureFlagRegistry() {
  return useQuery<FeatureFlagDef[]>(
    FEATURE_FLAG_REGISTRY_KEY,
    async () => {
      const { data } = await openerOrchestratorAxiosApi.get<{
        flags: FeatureFlagDef[];
      }>('/feature-flags');
      return parseRegistryResponse(data);
    },
    {
      // The registry only changes on a deploy, so it is worth caching — but NOT
      // worth an empty fallback: a drawer that renders no controls because the
      // fetch failed would read as "this build has no flags", which is a
      // different and much worse statement.
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      // ALWAYS refetch when a drawer opens. A cached registry from before a
      // deploy is missing that deploy's new flags, and this UI treats "key in
      // the group, absent from the registry" as RETIRED — offering to delete
      // it. One small GET, only on open, is the price of never destroying a
      // live opt-in on a stale guess.
      refetchOnMount: 'always',
    },
  );
}

/**
 * Whether two group opt-in maps differ, comparing keys AND values.
 *
 * Used to decide whether to send `featureFlags` at all. Sending it
 * unconditionally is not safe: an unknown key in the map is a 400, and during a
 * flag's retirement window every group still holding the retired key would be
 * un-editable — the operator could not even rename it, and no control would
 * render to clear the key.
 */
export function featureFlagsDiffer(
  a: Record<string, boolean> | undefined,
  b: Record<string, boolean> | undefined,
): boolean {
  const left = a ?? {};
  const right = b ?? {};
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const k of keys) {
    if (left[k] !== right[k]) return true;
  }
  return false;
}

/** Registry entries grouped by `area`, each group's flags in registry order. */
export function groupFlagsByArea(
  flags: FeatureFlagDef[],
): Array<[string, FeatureFlagDef[]]> {
  const byArea = new Map<string, FeatureFlagDef[]>();
  for (const flag of flags) {
    const bucket = byArea.get(flag.area);
    if (bucket) bucket.push(flag);
    else byArea.set(flag.area, [flag]);
  }
  return [...byArea.entries()];
}

/**
 * Whether the group's saved opt-ins differ from what its running pod booted
 * with, i.e. whether a restart is pending.
 *
 * Compares the RESOLVED set (what the group would boot with now) against the
 * set the pod actually reports. Both are produced server-side — this only
 * compares them, it never re-derives the chain.
 */
export function hasPendingFlagRestart(
  resolved: string[] | undefined,
  booted: string[] | undefined,
): boolean {
  if (!resolved || !booted) return false;
  const a = [...resolved].sort();
  const b = [...booted].sort();
  return a.length !== b.length || a.some((k, i) => k !== b[i]);
}
